import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { inr, fdate, INST_COLOR } from "@/components/FeeLedgerDialog";
import { toast } from "sonner";
import { ChevronRight, Banknote, Landmark, Smartphone, CreditCard, BadgeCheck, Loader2, X, BellRing } from "lucide-react";

const TYPES = [["all", "All"], ["term", "Term Fees"], ["annual", "Annual Fee"], ["transport", "Transport Fee"]];
const TYPE_LABEL = { term: "Term fee", annual: "Annual fee", transport: "Transport", activity: "Activity", other: "Other" };

function HowToPay({ canManage, onManual, online }) {
  return (
    <div data-testid="how-to-pay" className="mt-4 rounded-xl bg-violet-50/70 border border-violet-100 p-4 space-y-3">
      <div className="font-display font-bold text-slate-900">How to pay</div>
      <div className="grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
        <div className="flex items-center gap-2"><Banknote size={16} className="text-violet-700" /> Cash or cheque at the school office</div>
        <div className="flex items-center gap-2"><Landmark size={16} className="text-violet-700" /> Bank transfer, then upload proof in the parent app</div>
        <div className="flex items-center gap-2"><Smartphone size={16} className="text-violet-700" /> UPI, then upload the transaction proof</div>
        <div className="flex items-center gap-2"><CreditCard size={16} className="text-violet-700" />
          {online.includes("razorpay") ? <span data-testid="razorpay-enabled">Razorpay online payment is enabled — parents can pay from their Fees page</span>
            : <span data-testid="razorpay-disabled" className="text-slate-500">Razorpay online payment is not enabled yet</span>}</div>
      </div>
      {canManage && (
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-violet-100">
          <span className="text-xs text-slate-500 flex-1">Received cash or a bank transfer? Verify it first, then record it against the installment.</span>
          <Button data-testid="mark-manually-paid" className="btn-brand" onClick={onManual}><BadgeCheck size={15} className="mr-1" /> Mark as manually paid</Button>
        </div>
      )}
    </div>
  );
}

function Breakdown({ row, canManage, onManual, onClose, online }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/fee-mgmt/plans/${row.plan_id}/breakdown`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e))); }, [row.plan_id, row.pending]);
  return (
    <div data-testid={`breakdown-${row.plan_id}`} className="bg-white rounded-2xl shadow-sm p-5 border-l-4 border-violet-500">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1"><div className="font-display text-lg font-bold text-slate-900">{row.student_name}</div>
          <div className="text-xs text-slate-500">{row.class_name}-{row.section} • {row.academic_year}</div></div>
        <button data-testid="breakdown-close" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>
      {!d ? <Loader2 className="animate-spin text-violet-500 mx-auto my-4" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="py-2">Fee item</th><th>Amount</th><th>Paid</th><th>Pending</th><th>Due date</th><th>Status</th></tr></thead>
            <tbody>
              {d.lines.map((l) => (
                <tr key={l.key} data-testid={`line-${l.key}`} className="border-t border-slate-100">
                  <td className="py-2 font-semibold">{l.label}</td><td>{inr(l.amount)}</td><td className="text-emerald-700">{inr(l.paid)}</td>
                  <td className={l.pending > 0 ? "text-rose-600 font-semibold" : ""}>{inr(l.pending)}</td><td>{fdate(l.due_date)}</td>
                  <td><Badge className={INST_COLOR[l.status]}>{l.status}</Badge></td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-200 font-bold"><td className="py-2">Total (after discounts)</td><td>{inr(d.plan.net_total)}</td><td className="text-emerald-700">{inr(d.plan.paid)}</td><td className="text-rose-600">{inr(d.plan.pending)}</td><td colSpan={2}>{d.plan.late_fee_due > 0 && <span className="text-xs text-rose-600">+ late fee {inr(d.plan.late_fee_due)}</span>}</td></tr>
            </tbody>
          </table>
        </div>
      )}
      <HowToPay canManage={canManage} onManual={() => onManual(row.plan_id)} online={online} />
    </div>
  );
}

function RemindButton({ row, onSent }) {
  const [busy, setBusy] = useState(false);
  const recent = row.last_reminded_at && Date.now() - new Date(row.last_reminded_at).getTime() < 3600000;
  async function send(e) {
    e.stopPropagation();
    setBusy(true);
    try { const r = await api.post(`/fee-mgmt/plans/${row.plan_id}/remind`); toast.success(`Reminder sent to ${row.student_name}'s parent`); onSent(row.plan_id, r.data.last_reminded_at); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  return (
    <div className="flex flex-col items-start">
      <button data-testid={`remind-${row.plan_id}`} onClick={send} disabled={busy || recent}
        className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50 transition-colors whitespace-nowrap">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} {recent ? "Reminded" : "Remind parent"}
      </button>
      {row.last_reminded_at && <span className="text-[10px] text-slate-400 mt-0.5">last {fdate(row.last_reminded_at)}</span>}
    </div>
  );
}

export function UnpaidPanel({ branchId, canManage, onManual, refreshKey }) {
  const [type, setType] = useState("all");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [online, setOnline] = useState([]);
  useEffect(() => { api.get("/payments/providers").then((r) => setOnline(r.data.map((p) => p.id))).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/fee-mgmt/unpaid", { params: { fee_type: type, ...(branchId ? { branch_id: branchId } : {}) } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [type, branchId, refreshKey]);
  const sel = rows?.find((r) => r.plan_id === open);
  return (
    <div data-testid="unpaid-panel" className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h3 className="font-display text-lg font-bold text-slate-900 flex-1">Students with unpaid fees {rows && <span className="text-sm font-normal text-slate-500">({rows.length})</span>}</h3>
          {TYPES.map(([k, l]) => (
            <button key={k} data-testid={`unpaid-filter-${k}`} onClick={() => { setType(k); setOpen(null); }}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${type === k ? "bg-violet-600 text-white" : "bg-violet-50 text-violet-700 hover:bg-violet-100"}`}>{l}</button>
          ))}
        </div>
        {!rows ? <Loader2 className="animate-spin text-violet-500 mx-auto my-4" /> : rows.length === 0 ? <div className="text-sm text-slate-500 py-4">No unpaid fees for this filter.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th className="py-2">Student</th><th>Class</th><th>Section</th><th>Pending</th><th>Due date</th><th>Fee type</th><th>Reminder</th><th /></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.plan_id} data-testid={`unpaid-row-${r.plan_id}`} onClick={() => setOpen(open === r.plan_id ? null : r.plan_id)}
                  className={`border-t border-slate-100 cursor-pointer transition-colors ${open === r.plan_id ? "bg-violet-50" : "hover:bg-slate-50"}`}>
                  <td className="py-2.5 font-semibold">{r.student_name}</td><td>{r.class_name}</td><td>{r.section}</td>
                  <td className="text-rose-600 font-semibold">{inr(r.pending)}</td>
                  <td className={r.overdue ? "text-rose-600" : ""}>{fdate(r.due_date)}{r.overdue && " • overdue"}</td>
                  <td><div className="flex flex-wrap gap-1">{r.fee_types.map((t) => <Badge key={t} variant="outline" className="text-[10px]">{TYPE_LABEL[t]}</Badge>)}</div></td>
                  <td><RemindButton row={r} onSent={(id, at) => setRows((rs) => rs.map((x) => (x.plan_id === id ? { ...x, last_reminded_at: at } : x)))} /></td>
                  <td><ChevronRight size={16} className={`text-slate-400 transition-transform ${open === r.plan_id ? "rotate-90" : ""}`} /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
      {sel && <Breakdown row={sel} canManage={canManage} onManual={onManual} onClose={() => setOpen(null)} online={online} />}
    </div>
  );
}
